import MyColor from 'utils/MyColor';

/* 배달범위(그리기/반경) 렌더러 — OSM 타일 배경 + 도형 SVG 오버레이
 *
 * 네이버지도 타일은 CORS 미지원이라 html2canvas(PDF 추출) 캡처가 불가능합니다.
 * OpenStreetMap 타일은 CORS(Access-Control-Allow-Origin: *)를 지원하므로,
 * OSM 타일을 배경으로 깔고 사장님웹에서 설정된 도형(설정값 그대로)을 SVG로 겹쳐 그립니다.
 * 타일 로드에 실패해도 도형은 항상 렌더링됩니다. (fallback: 단색 배경 + 도형)
 *
 * - POLYGON(그리기): deliveryZone.regions[].shapes 의 polygon/circle을 색상 그대로 표시
 * - RADIUS(반경): radiusList[] 의 원들을 매장 좌표 중심으로 표시
 * - 좌표계: 지도 타일과 동일한 Web Mercator (px @ zoom z) — 배경/도형 어긋남 없음
 *
 * 주의: StoreInfo 문서 페이지 분할 로직(ESignDocumentTemplate) 때문에
 * BusinessHours 등과 동일하게 "함수 호출" 패턴으로 사용합니다. (JSX 컴포넌트 아님)
 */

// 사장님웹 그리기 화면과 유사한 fallback 팔레트 (region.color가 없을 때 사용)
const FALLBACK_COLORS = ['#F76626', '#0073ED', '#0BA800', '#C68700', '#E4312B', '#3C5AD1'];

const TILE_SIZE = 256;
const WORLD = 256; // zoom 0 world size (px)
const EARTH_CIRCUMFERENCE_KM = 40075.016686;
const RENDER_WIDTH = 640; // 도형/선 굵기 산정 기준 px
// 계약서 페이지에서 지도 박스의 고정 크기 (본문 폭 876px 기준 높이 620px)
// A4 페이지(내용 높이 1254px)에서 배달 정보 항목들과 함께 잘리지 않게 들어가는 최대 높이
const MAP_BOX_WIDTH = 876;
const MAP_BOX_HEIGHT = 620;
const TARGET_ASPECT = MAP_BOX_WIDTH / MAP_BOX_HEIGHT;
const MIN_ZOOM = 3;
const MAX_ZOOM = 17;

const osmTileUrl = (z, x, y) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

const toNumber = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

// 서버 확정 스펙은 {latitude, longitude}, 사장님웹 내부 포맷은 {lat, lng} — 둘 다 허용
const pointLat = (p) => toNumber(p?.latitude ?? p?.lat);
const pointLng = (p) => toNumber(p?.longitude ?? p?.lng);

/** lat/lng -> Web Mercator world 좌표 (zoom 0, 0~256) */
const latLngToWorld = (lat, lng) => {
  const sin = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: WORLD * (0.5 + lng / 360),
    y: WORLD * (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)),
  };
};

/** km -> world 좌표 단위 (zoom 0 기준, 해당 위도에서) */
const kmToWorldUnits = (km, lat) => {
  const cos = Math.cos((lat * Math.PI) / 180) || 1;
  return (km / (EARTH_CIRCUMFERENCE_KM * cos)) * WORLD;
};

const normalizeColor = (color, index) => {
  if (typeof color === 'string' && color.trim()) {
    const c = color.trim();
    return c.startsWith('#') ? c : `#${c}`;
  }
  return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
};

/**
 * @param {object} params
 * @param {Array} [params.regions] - deliveryZone.regions (POLYGON)
 * @param {Array} [params.radiusList] - 반경 원 목록 (RADIUS)
 * @param {number|string|null} [params.latitude] - 매장 위도
 * @param {number|string|null} [params.longitude] - 매장 경도
 */
export const buildDeliveryShapeSvg = ({ regions = [], radiusList = [], latitude, longitude }) => {
  const storeLat = toNumber(latitude);
  const storeLng = toNumber(longitude);

  // ---------- 도형 수집 (world 좌표, zoom 0) ----------
  const polygons = []; // { points: [{x,y}], color, active }
  const circles = []; // { x, y, r, color, active }

  regions.forEach((region, i) => {
    const color = normalizeColor(region.color, i);
    (region.shapes || []).forEach((shape) => {
      const type = (shape.type || '').toLowerCase();

      // 서버 ShapeResponse에는 radius가 없고 circle도 paths(다각형 좌표)로 내려옴
      // radius가 있는 응답(구버전/사장님웹 포맷)만 원으로 그리고, 그 외에는 paths 사용
      if (type === 'circle' && shape.center) {
        const lat = pointLat(shape.center);
        const lng = pointLng(shape.center);
        const radiusM = toNumber(shape.radius);
        if (lat != null && lng != null && radiusM != null) {
          const c = latLngToWorld(lat, lng);
          circles.push({
            ...c,
            r: kmToWorldUnits(radiusM / 1000, lat),
            color,
            active: region.active,
          });
          return;
        }
      }

      (shape.paths || []).forEach((path) => {
        const points = (path || [])
          .map((p) => ({ lat: pointLat(p), lng: pointLng(p) }))
          .filter((p) => p.lat != null && p.lng != null)
          .map((p) => latLngToWorld(p.lat, p.lng));
        if (points.length >= 3) {
          polygons.push({ points, color, active: region.active });
        }
      });
    });
  });

  if (storeLat != null && storeLng != null) {
    const center = latLngToWorld(storeLat, storeLng);
    radiusList.forEach((item, i) => {
      const radiusKm = toNumber(item.radiusKm);
      if (radiusKm == null || radiusKm <= 0) return;
      circles.push({
        ...center,
        r: kmToWorldUnits(radiusKm, storeLat),
        color: normalizeColor(null, i),
        active: item.isActive !== false,
      });
    });
  }

  if (polygons.length === 0 && circles.length === 0) return null;

  // ---------- bounding box (world 좌표) ----------
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const include = (x, y) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };

  polygons.forEach((p) => p.points.forEach((pt) => include(pt.x, pt.y)));
  circles.forEach((c) => {
    include(c.x - c.r, c.y - c.r);
    include(c.x + c.r, c.y + c.r);
  });

  let storePoint = null;
  if (storeLat != null && storeLng != null) {
    storePoint = latLngToWorld(storeLat, storeLng);
    include(storePoint.x, storePoint.y);
  }

  // 여백 8%
  const spanX = Math.max(maxX - minX, 1e-9);
  const spanY = Math.max(maxY - minY, 1e-9);
  const pad = Math.max(spanX, spanY) * 0.08;
  minX -= pad;
  minY -= pad;
  let spanW = spanX + pad * 2;
  let spanH = spanY + pad * 2;

  // ---------- 고정 비율 박스로 확장 ----------
  // 지도 높이가 매장(도형 모양)마다 달라지지 않도록, bbox를 TARGET_ASPECT
  // 비율에 맞춰 짧은 축을 중앙 기준으로 넓힌다 → 지도 박스는 항상 같은 크기
  if (spanW / spanH < TARGET_ASPECT) {
    const newW = spanH * TARGET_ASPECT;
    minX -= (newW - spanW) / 2;
    spanW = newW;
  } else {
    const newH = spanW / TARGET_ASPECT;
    minY -= (newH - spanH) / 2;
    spanH = newH;
  }

  // ---------- zoom 선택: bbox가 렌더 폭(~640px)에 맞는 최대 줌 ----------
  const maxSpan = Math.max(spanW, spanH);
  let zoom = Math.floor(Math.log2(RENDER_WIDTH / maxSpan));
  zoom = Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
  const scale = Math.pow(2, zoom);

  // px 좌표로 변환 (zoom z 기준)
  // ※ SVG 좌표는 원점(bbox 좌상단) 기준 로컬 좌표 사용 — zoom 13+에서 절대 좌표가
  //   수백만 단위가 되면 브라우저 float 정밀도 한계로 원/곡선이 깨져 보이는 문제 방지
  const originX = minX * scale;
  const originY = minY * scale;
  const px = (v) => v * scale;
  const localX = (v) => v * scale - originX;
  const localY = (v) => v * scale - originY;
  const width = px(spanW);
  const height = px(spanH);
  const unit = width / RENDER_WIDTH;

  // ---------- OSM 타일 목록 (컨테이너 % 배치) ----------
  const tiles = [];
  const maxTileIndex = scale - 1; // zoom z 타일 개수 = 2^z
  const tileMinX = Math.floor(originX / TILE_SIZE);
  const tileMaxX = Math.floor((originX + width) / TILE_SIZE);
  const tileMinY = Math.max(Math.floor(originY / TILE_SIZE), 0);
  const tileMaxY = Math.min(Math.floor((originY + height) / TILE_SIZE), maxTileIndex);

  for (let tx = tileMinX; tx <= tileMaxX; tx++) {
    for (let ty = tileMinY; ty <= tileMaxY; ty++) {
      // 경도 wrap (음수/초과 타일 인덱스 보정)
      const wrappedX = ((tx % scale) + scale) % scale;
      tiles.push({
        key: `${zoom}-${tx}-${ty}`,
        src: osmTileUrl(zoom, wrappedX, ty),
        leftPct: ((tx * TILE_SIZE - originX) / width) * 100,
        topPct: ((ty * TILE_SIZE - originY) / height) * 100,
        widthPct: (TILE_SIZE / width) * 100,
        heightPct: (TILE_SIZE / height) * 100,
      });
    }
  }

  return {
    viewBox: `0 0 ${width} ${height}`,
    aspectRatio: width / height,
    unit,
    tiles,
    polygons: polygons.map((p) => ({
      ...p,
      points: p.points.map((v) => ({ x: localX(v.x), y: localY(v.y) })),
    })),
    circles: circles.map((c) => ({ ...c, x: localX(c.x), y: localY(c.y), r: px(c.r) })),
    storePoint: storePoint && { x: localX(storePoint.x), y: localY(storePoint.y) },
  };
};

/** 지도 배경 + 도형 SVG 엘리먼트 (계약서/PDF 공용) */
export const DeliveryShapeSvg = ({ svgModel }) => {
  if (!svgModel) return null;
  const { viewBox, aspectRatio, unit, tiles, polygons, circles, storePoint } = svgModel;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: `${aspectRatio}`,
        border: `1px solid ${MyColor.gray10}`,
        borderRadius: 8,
        backgroundColor: MyColor.grayF9F9FA,
        overflow: 'hidden',
      }}
    >
      {/* OSM 타일 배경 — 로드 실패 시 해당 타일만 숨김 (도형은 항상 표시) */}
      {tiles.map((tile) => (
        <img
          key={tile.key}
          src={tile.src}
          alt=""
          crossOrigin="anonymous"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
          style={{
            position: 'absolute',
            left: `${tile.leftPct}%`,
            top: `${tile.topPct}%`,
            width: `${tile.widthPct}%`,
            height: `${tile.heightPct}%`,
          }}
        />
      ))}

      {/* 도형 오버레이 (배경 타일과 동일한 Web Mercator px 좌표계) */}
      <svg
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}
      >
        {polygons.map((polygon, i) => (
          <polygon
            key={`polygon-${i}`}
            points={polygon.points.map((p) => `${p.x},${p.y}`).join(' ')}
            fill={polygon.color}
            fillOpacity={0.25}
            stroke={polygon.color}
            strokeOpacity={1}
            strokeWidth={unit * 2}
          />
        ))}
        {circles.map((circle, i) => (
          <circle
            key={`circle-${i}`}
            cx={circle.x}
            cy={circle.y}
            r={circle.r}
            fill={circle.color}
            fillOpacity={0.15}
            stroke={circle.color}
            strokeOpacity={1}
            strokeWidth={unit * 2}
          />
        ))}
        {storePoint && (
          <g>
            <circle cx={storePoint.x} cy={storePoint.y} r={unit * 7} fill={MyColor.brand40} />
            <circle cx={storePoint.x} cy={storePoint.y} r={unit * 3} fill="#fff" />
          </g>
        )}
      </svg>

      {/* OSM 라이선스 표기 (필수) */}
      <div
        style={{
          position: 'absolute',
          right: 4,
          bottom: 2,
          fontSize: 9,
          color: MyColor.gray60,
          backgroundColor: 'rgba(255,255,255,0.7)',
          padding: '0 4px',
          borderRadius: 3,
        }}
      >
        © OpenStreetMap contributors
      </div>
    </div>
  );
};
